<template>
  <el-table
      :data="serviceImplementationView"
      border>
    <el-table-column
        width="200"
        prop="name"
        label="サービス実装名"
        fixed="left">
      <template #default="{row}">
        <table-inline>
          <text-field-like
              :value="row.name"
              :value-id="row.id">
            <template #default="{value}">
              {{ value }}
              <error-popover placement="right" :errors="row.errors"></error-popover>
              <update-icon :type="row.updateType" small>
                <template #tooltip="{tooltip}">
                  最新バージョンの個別辞書「{{ row.dictionary && row.dictionary.name }}」でサービス構成モデルが{{ tooltip }}
                </template>
                <template #tooltip-append>
                  <v-btn small outlined class="primary white--text ml-2 px-2" @click="handleUpdate(row)" v-if="row.updateType === 'update'">
                    <v-icon small class="mr-1">mdi-arrow-top-right-thick</v-icon>
                    更新する
                  </v-btn>
                  <v-btn small outlined class="primary white--text ml-2 px-2" @click="openDeleteDialog(row)" v-if="row.updateType === 'deleted'">
                    <v-icon small class="mr-1">mdi-close-thick</v-icon>
                    削除する
                  </v-btn>
                </template>
              </update-icon>
            </template>
          </text-field-like>
        </table-inline>
      </template>
    </el-table-column>

    <el-table-column
        width="200"
        prop="description"
        label="説明">
      <template #default="{row}">
        <table-inline>
          <text-field-like
              :value="row.description"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </table-inline>
      </template>
    </el-table-column>

    <el-table-column
        min-width="150"
        prop="localId"
        label="内部ID">
      <template #default="{row}">
        <table-inline>
          <local-id-field
              :value="row.localId">
          </local-id-field>
        </table-inline>
      </template>
    </el-table-column>

    <el-table-column
        min-width="150"
        prop="remarks"
        label="備考">
      <template #default="{row}">
        <table-inline>
          <text-field-like
              :value="row.remarks"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </table-inline>
      </template>
    </el-table-column>

    <el-table-column
        min-width="150"
        prop="controllerName"
        label="コントローラ">
    </el-table-column>

    <el-table-column
        min-width="100"
        prop="status"
        label="ステータス"
        align="center">
      <template #default="{row}">
        <table-inline>
          <text-field-like
              :value="serviceImplementationStatuses[row.status]"
              :placeholder="row.status || '未設定'"
              align="center">
          </text-field-like>
        </table-inline>
      </template>
    </el-table-column>

    <el-table-column
        width="50"
        align="center"
        prop="publishing"
        label="公開">
      <template #default="{row}">
        <readonly-checkbox :value="row.publishing"></readonly-checkbox>
      </template>
    </el-table-column>

    <el-table-column
        prop="createdAt"
        label="登録日時"
        width="100">
      <template #default="{row}">
        {{ formatDateTime(row.createdAt) }}
      </template>
    </el-table-column>

    <el-table-column
        width="93"
        align="right"
        fixed="right">
      <template #default="{row}">
        <icon-btns>
          <!-- 編集モード -->
          <template v-if="isEditable">
            <icon-btn
                type="delete"
                @click="openDeleteDialog(row)">
            </icon-btn>
          </template>

          <!-- 閲覧/編集 -->
          <icon-btn
              :type="isEditable ? 'edit' : 'view'"
              :to="{ name: alterRouteName('ServiceImplementationShow'), params: {serviceImplementationId: row.id}}">
          </icon-btn>
        </icon-btns>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import BetterSet from "betterset";
import gql from "graphql-tag";
import {mapState, mapActions, mapGetters} from "vuex";
import Formatter from "@/utils/Formatter";
import { SERVICE_IMPLEMENTATION_STATUSES } from "@/constants";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import LocalIdField from "@/components/molecules/LocalIdField.vue";
import UpdateIcon from "@/components/molecules/UpdateIcon.vue";
import Swal from "sweetalert2";

export default {
  name: 'ServiceImplementationTable',
  mixins: [
    PathInfoMixin,
    Formatter
  ],
  components: {
    UpdateIcon,
    LocalIdField,
    IconBtn,
    IconBtns,
    ErrorPopover,
    ReadonlyCheckbox,
    TextFieldLike,
    TableInline,
  },
  props: {
    serviceImplementations: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      serviceComponentModels: [],
      serviceImplementationStatuses: SERVICE_IMPLEMENTATION_STATUSES
    };
  },
  computed: {
    ...mapState('hcs/controller', ['controllers']),
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    serviceImplementationView() {
      return this.serviceImplementations.map(si => {
        const scm = this.serviceComponentModels.find(scm => scm.id === si.serviceComponentModelId);
        const controller = this.controllers.find(c => c.id === si.controllerId);
        const errors = [];
        if (si.eventImplementationsCount === 0) {
          errors.push({
            level: 'warn',
            message: 'イベント実装が登録されていません'
          });
        }
        if (si.triggerImplementationsCount === 0) {
          errors.push({
            level: 'info',
            message: 'トリガとなるイベント実装が登録されていません'
          });
        }
        const updateType = (() => {
          const latestPublished = scm?.latestPublished;
          if(!latestPublished) {
            return 'deleted';
          }
          return latestPublished?.id === scm?.id ? '' : 'update'
        })();
        return {
          ...si,
          serviceComponentModel: scm,
          dictionary: scm?.dictionary,
          name: scm?.name,
          description: scm?.description,
          controllerName: controller?.name || si.controllerId,
          updateType,
          errors
        };
      });
    }
  },
  methods: {
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
    }),
    openDeleteDialog(row) {
      this.$emit('openDeleteDialog', row)
    },
    async handleUpdate(row) {
      const {value: confirm} = await Swal.fire({
        title: 'サービス実装の更新',
        html: `本当にサービス実装 <b>${this.escapeHtml(row.name)}</b> を更新しますか？`,
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ",
      });
      if (!confirm) {
        return;
      }

      this.$emit('update', {
        serviceComponentModelId: row.serviceComponentModel?.latestPublished?.id,
        controllerId: row.controllerId,
        updateType: row.updateType,
      });
    },
  },
  watch: {
    serviceImplementations: {
      immediate: true,
      async handler(serviceImplementations) {
        // サービス実装に使用されているサービス構成モデルを取得
        const serviceComponentModelIds = serviceImplementations
            .map(si => si.serviceComponentModelId)
            .reduce((set, value) => {set.add(value); return set;}, new BetterSet())
            .asArray();
        if(serviceComponentModelIds.length === 0) {
          this.serviceComponentModels = [];
          return;
        }

        const {serviceComponentModels} = await this.gqlQuery({
          query: gql`
            query FindServiceComponentModels(
                $serviceComponentModelIds: [ID!]!,
            ) {
                serviceComponentModels: serviceComponentModelInBulk(serviceComponentModelIds: $serviceComponentModelIds) {
                    id
                    name
                    description
                    latestPublished {
                        id
                    }
                    dictionary {
                        name
                    }
                }
            }`,
          variables: {
            serviceComponentModelIds
          },
        });
        this.serviceComponentModels = serviceComponentModels;
      }
    }
  }
}
</script>
